import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Package2, 
  X, 
  Upload, 
  Save, 
  ArrowLeft, 
  BookOpen,
  History,
  FolderOpen,
  MessageCircle,
  Users,
  Link as LinkIcon,
  AlertCircle,
  User
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Sidebar from '../components/layout/Sidebar';
import { stockMenuItems } from './StokFlow'; // Import stockMenuItems from StokFlow
import { createClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';
import { uploadImage } from '../lib/imageUpload';

// Initialize Supabase clients
const supabaseUrl = import.meta.env.VITE_FILE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_FILE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Initialize User Profiles Supabase client
const userProfilesUrl = import.meta.env.VITE_SUPABASE_URL;
const userProfilesKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const userProfilesSupabase = createClient(userProfilesUrl, userProfilesKey);

const StokProductEdit = () => {
  const navigate = useNavigate();
  const { productId } = useParams(); // Get productId from URL
  const fileInputRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [imageSource, setImageSource] = useState('file');
  const [infoBoxOpen, setInfoBoxOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  
  const [productData, setProductData] = useState({
    name: '',
    description: '',
    stock: '',
    location: '',
    purchaseLink: '',
    image: null,
    imageUrl: '',
    updated_by: ''
  });
  
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // Fetch product data when component mounts
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        
        if (!productId) {
          setError('Ürün ID\'si bulunamadı');
          setInitialLoading(false);
          return;
        }
        
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('id', productId)
          .single();
          
        if (error) throw error;
        
        if (!data) {
          setError('Ürün bulunamadı');
          setInitialLoading(false);
          return;
        }
        
        // Set product data
        setProductData({
          name: data.name || '',
          description: data.description || '',
          stock: data.stock?.toString() || '0',
          location: data.location || '',
          purchaseLink: data.purchase_link || '',
          image: null,
          imageUrl: data.image_url || '',
        });
        
        // Set image source based on whether there's an existing image
        if (data.image_url) {
          setImageSource('url');
        }
        
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Ürün bilgileri yüklenirken bir hata oluştu: ' + (err.message || 'Lütfen tekrar deneyin'));
      } finally {
        setInitialLoading(false);
      }
    };
    
    fetchProduct();
  }, [productId]);
  
  // Add useEffect for fetching user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // First get the user from the auth client
        const { data: { user }, error: authError } = await userProfilesSupabase.auth.getUser();
        
        if (authError) {
          console.error('Auth error:', authError);
          return;
        }

        if (user) {
          // Then fetch the user profile from user_profiles table
          const { data, error: profileError } = await userProfilesSupabase
            .from('user_profiles')
            .select('first_name, last_name')
            .eq('id', user.id)
            .single();

          if (profileError) {
            console.error('Profile fetch error:', profileError);
            return;
          }

          if (data) {
            setUserProfile(data);
          } else {
            console.warn('No user profile found for user:', user.id);
          }
        } else {
          console.warn('No authenticated user found');
        }
      } catch (error) {
        console.error('Error in fetchUserProfile:', error);
      }
    };

    fetchUserProfile();
  }, []);
  
  // Form field handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProductData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProductData(prev => ({
        ...prev,
        image: e.target.files[0],
        imageUrl: '' // Clear URL when file is selected
      }));
      setImageSource('file');
    }
  };

  const handleImageUrlChange = (e) => {
    const { value } = e.target;
    setProductData(prev => ({
      ...prev,
      imageUrl: value,
      image: null // Clear file when URL is entered
    }));
    setImageSource('url');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setProductData(prev => ({
        ...prev,
        image: e.dataTransfer.files[0],
        imageUrl: ''
      }));
      setImageSource('file');
    }
  };

  const handleDropzoneClick = () => {
    fileInputRef.current.click();
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    
    // If empty, allow it
    if (value === '') {
      setProductData(prev => ({
        ...prev,
        location: ''
      }));
      return;
    }
    
    // Check first character - must be a letter
    if (value.length >= 1) {
      const firstChar = value.charAt(0);
      if (!/[a-zA-Z]/.test(firstChar)) {
        return; // Reject input if first character isn't a letter
      }
    }
    
    let formattedValue = '';
    
    // Process the input based on its length
    if (value.length === 1) {
      // First character should be uppercase letter
      formattedValue = value.toUpperCase() + '-';
    } else if (value.length === 2) {
      // Second character should be a hyphen
      if (value.charAt(1) !== '-') {
        // If user typed something other than hyphen, replace it with hyphen
        formattedValue = value.charAt(0).toUpperCase() + '-';
      } else {
        formattedValue = value.charAt(0).toUpperCase() + '-';
      }
    } else if (value.length >= 3 && value.length <= 4) {
      // Characters 3-4 should be digits
      const prefix = value.substring(0, 2); // A-
      const digits = value.substring(2);
      
      // Only allow digits after the hyphen
      if (!/^\d*$/.test(digits)) {
        // If input contains non-digits after hyphen, reject it
        return;
      }
      
      formattedValue = prefix + digits;
      
      // Automatically add underscore after two digits
      if (digits.length === 2) {
        formattedValue += '_';
      }
    } else if (value.length >= 5) {
      // Get the parts
      const prefix = value.substring(0, 2); // A-
      const digits = value.substring(2, 4);
      const underscore = value.substring(4, 5);
      const lastChar = value.substring(5, 6);
      
      // Validate underscore
      if (underscore !== '_') {
        // If 5th character isn't underscore, make it one
        formattedValue = prefix + digits + '_';
      } else {
        formattedValue = prefix + digits + '_';
        
        // Last character must be 1, 2, or 3
        if (lastChar && !['1', '2', '3'].includes(lastChar)) {
          // If last character isn't valid, reject it
          return;
        }
        
        if (lastChar) {
          formattedValue += lastChar;
        }
      }
    }
    
    setProductData(prev => ({
      ...prev,
      location: formattedValue
    }));
  };
  
  // Validation functions
  const validateForm = () => {
    if (!productData.name.trim()) {
      setError('Ürün adı girilmelidir');
      return false;
    }
    
    if (!productData.location || productData.location.length !== 6) {
      setError('Geçerli bir dolap konumu girilmelidir (A-12_1 formatında)');
      return false;
    }
    
    return true;
  };

  // Check if product name or location already exists (but exclude the current product)
  const checkDuplicates = async () => {
    try {
      // Check for duplicate product name
      const { data: nameData, error: nameError } = await supabase
        .from('products')
        .select('id')
        .eq('name', productData.name.trim())
        .neq('id', productId) // Exclude current product
        .limit(1);
      
      if (nameError) throw nameError;
      
      if (nameData && nameData.length > 0) {
        setError(`"${productData.name}" adında başka bir ürün zaten mevcut. Lütfen farklı bir isim seçin.`);
        return false;
      }
      
      // Check for duplicate location
      const { data: locationData, error: locationError } = await supabase
        .from('products')
        .select('id, name')
        .eq('location', productData.location)
        .neq('id', productId) // Exclude current product
        .limit(1);
      
      if (locationError) throw locationError;
      
      if (locationData && locationData.length > 0) {
        setError(`"${productData.location}" konumu zaten "${locationData[0].name}" ürünü tarafından kullanılıyor. Lütfen farklı bir konum seçin.`);
        return false;
      }
      
      // No duplicates found
      return true;
    } catch (error) {
      console.error('Duplicate check error:', error);
      setError('Mevcut ürünler kontrol edilirken bir hata oluştu.');
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset states
    setError(null);
    setSuccess(false);
    
    // Validate form data
    if (!validateForm()) {
      return;
    }
    
    // Check for duplicates before proceeding
    const isDuplicatesFree = await checkDuplicates();
    if (!isDuplicatesFree) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Handle image upload if needed
      let finalImageUrl = productData.imageUrl;
      
      if (productData.image) {
        try {
          finalImageUrl = await uploadImage(productData.image);
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          setError('Görsel yüklenemedi, ancak ürün bilgileri kaydedilecek.');
        }
      }

      // Use userProfile for updater name
      let updaterName = 'Belirtilmemiş kullanıcı';
      if (userProfile) {
        updaterName = `${userProfile.first_name} ${userProfile.last_name}`;
      }
      
      // Get current product data first
      const { data: currentProduct, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .single();

      if (fetchError) throw fetchError;

      // Track changes
      const changes = {};
      
      // Compare and track changes
      if (currentProduct.name !== productData.name) {
        changes.name = {
          old: currentProduct.name,
          new: productData.name
        };
      }
      
      if (currentProduct.description !== productData.description) {
        changes.description = {
          old: currentProduct.description || '',
          new: productData.description || ''
        };
      }
      
      if (currentProduct.location !== productData.location) {
        changes.location = {
          old: currentProduct.location,
          new: productData.location
        };
      }
      
      // Fix purchase link comparison
      const oldPurchaseLink = currentProduct.purchase_link || '';
      const newPurchaseLink = productData.purchaseLink || '';
      
      console.log('Purchase Link Comparison:', {
        old: oldPurchaseLink,
        new: newPurchaseLink,
        changed: oldPurchaseLink !== newPurchaseLink
      });
      
      if (oldPurchaseLink !== newPurchaseLink) {
        changes.purchase_link = {
          old: oldPurchaseLink,
          new: newPurchaseLink
        };
      }
      
      // Check if image was actually changed
      const imageChanged = 
        (productData.image !== null) || // New file was uploaded
        (productData.imageUrl !== currentProduct.image_url) || // URL was changed
        (imageSource === 'url' && productData.imageUrl !== currentProduct.image_url); // URL source was selected and URL is different

      if (imageChanged) {
        changes.image_url = {
          old: currentProduct.image_url || '',
          new: finalImageUrl || productData.imageUrl || ''
        };
      }

      // Prepare new history record
      const newHistoryRecord = {
        type: 'edit',
        amount: parseInt(productData.stock) || 0,
        previous_stock: parseInt(currentProduct.stock) || 0,
        new_stock: parseInt(productData.stock) || 0,
        updated_at: new Date().toISOString(),
        updated_by: updaterName,
        changes: Object.keys(changes).length > 0 ? changes : null,
        note: Object.keys(changes).length > 0 
          ? 'Ürün bilgileri güncellendi: ' + Object.keys(changes).map(key => {
              switch(key) {
                case 'name': return 'Ürün adı';
                case 'description': return 'Açıklama';
                case 'location': return 'Konum';
                case 'purchase_link': return 'Satın alma linki';
                case 'image_url': return 'Ürün görseli';
                default: return key;
              }
            }).join(', ')
          : 'Ürün bilgileri güncellendi'
      };

      // Create new stock_history array with the new record
      const updatedStockHistory = [
        ...(currentProduct.stock_history || []),
        newHistoryRecord
      ];

      // Update product in database
      const { data: updatedProduct, error: dbError } = await supabase
        .from('products')
        .update({
          name: productData.name,
          description: productData.description || null,
          stock: parseInt(productData.stock) || 0,
          location: productData.location,
          purchase_link: productData.purchaseLink || null,
          image_url: finalImageUrl || productData.imageUrl || null,
          updated_at: new Date().toISOString(),
          updated_by: updaterName,
          stock_history: updatedStockHistory
        })
        .eq('id', productId)
        .select();
      
      if (dbError) throw dbError;
      
      // Show success message
      setSuccess(true);
      
      // Reset form after successful submission
      setTimeout(() => {
        navigate('/stokflow/products');
      }, 1500);
      
    } catch (err) {
      console.error('Error updating product:', err);
      setError('Bir hata oluştu: ' + (err.message || 'Lütfen tekrar deneyin'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar 
        isOpen={isSidebarOpen} 
        onToggle={toggleSidebar} 
        customMenuItems={stockMenuItems}
        isStockPanel={true}
      />
      
      {/* Main Content */}
      <div className={`lg:pl-72 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:pl-72' : 'lg:pl-0'}`}>
        <Navbar onToggleSidebar={toggleSidebar} />
        <motion.main
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="py-6 px-4 sm:px-6 lg:px-8"
        >
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <button 
                  onClick={() => navigate('/stokflow/products')}
                  className="mr-4 p-2 rounded-lg bg-white dark:bg-gray-800 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Ürün Düzenle</h1>
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={() => navigate('/stokflow/products')} 
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                  disabled={loading}
                >
                  <X className="w-4 h-4" />
                  <span>İptal</span>
                </button>
                <button 
                  onClick={handleSubmit}
                  className={`px-4 py-2 ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white rounded-lg transition-colors flex items-center gap-2`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Kaydet</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Status Messages */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-md flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span>{error}</span>
              </div>
            )}
            
            {success && (
              <div className="mb-4 p-3 bg-green-100 border border-green-200 text-green-700 rounded-md flex items-center">
                <Save className="w-5 h-5 mr-2" />
                <span>Ürün başarıyla güncellendi! Yönlendiriliyorsunuz...</span>
              </div>
            )}

            {/* Loading State */}
            {initialLoading ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin inline-block w-8 h-8 border-4 border-gray-300 dark:border-gray-600 border-t-blue-600 dark:border-t-blue-500 rounded-full mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-300">Ürün bilgileri yükleniyor...</p>
                </div>
              </div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                {/* Information Box */}
                <div className="mb-6 border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden">
                  <button 
                    type="button"
                    onClick={() => setInfoBoxOpen(!infoBoxOpen)}
                    className="w-full p-4 bg-blue-50 dark:bg-blue-900/20 text-left flex items-center justify-between"
                  >
                    <h3 className="text-lg font-medium text-blue-800 dark:text-blue-300 flex items-center">
                      <AlertCircle className="w-5 h-5 mr-2 text-blue-500 dark:text-blue-400" />
                      Ürün Düzenleme Bilgilendirmesi
                    </h3>
                    <div className={`transform transition-transform duration-200 ${infoBoxOpen ? 'rotate-180' : ''}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {infoBoxOpen && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-t border-blue-200 dark:border-blue-800">
                      <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 ml-7 list-disc">
                        <li>Ürün adı ve dolap konumu benzersiz olmalıdır, mevcut diğer kayıtlarla aynı olamaz.</li>
                        <li>Dolap konumu <span className="font-mono bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded">A-12_1</span> formatında girilmelidir.</li>
                        <li>Stok miktarı her zaman 0 veya daha yüksek bir değer olmalıdır.</li>
                        <li>Ürün görseli dosya olarak yüklenebilir veya URL olarak eklenebilir.</li>
                        <li>Güncellemeler tarih ve kullanıcı bilgisi ile kaydedilecektir.</li>
                      </ul>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white border-b pb-2 dark:border-gray-700">Temel Bilgiler</h3>
                    
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ürün Adı *</label>
                        <input
                          type="text"
                          name="name"
                          value={productData.name}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10"
                          placeholder="Ürün adını girin"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ürün Açıklaması</label>
                        <textarea
                          name="description"
                          value={productData.description}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[150px] max-h-[300px]"
                          placeholder="Ürün açıklamasını girin (Zorunlu değil)"
                          style={{ resize: 'vertical' }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white border-b pb-2 dark:border-gray-700">Stok ve Konum Bilgileri</h3>
                    
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mevcut Stok *</label>
                        <input
                          type="number"
                          name="stock"
                          value={productData.stock}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10"
                          placeholder="0"
                          min="0"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dolap Konumu *</label>
                        <div className="relative">
                          <input
                            type="text"
                            name="location"
                            value={productData.location}
                            onChange={handleLocationChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10"
                            placeholder="Örn: A-12_1"
                            required
                            maxLength={6}
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {productData.location ? (
                                <span className={`${productData.location.length === 6 ? 'text-green-500 dark:text-green-400' : ''}`}>
                                  {productData.location.length}/6
                                </span>
                              ) : 'A-00_1'}
                            </span>
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Format: <span className="font-mono">Harf-Rakam(2)_Rakam(1-3)</span>
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Satın Alma Linki</label>
                        <div className="flex items-center relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <LinkIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          </div>
                          <input
                            type="url"
                            name="purchaseLink"
                            value={productData.purchaseLink}
                            onChange={handleChange}
                            className="w-full pl-10 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10"
                            placeholder="https://example.com/product"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Ürün Görseli */}
                <div className="mt-8">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white border-b pb-2 mb-4 dark:border-gray-700">Ürün Görseli</h3>
                  
                  <div className="flex flex-col items-center space-y-4">
                    {/* Image Source Toggle */}
                    <div className="flex justify-center space-x-4 mb-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={imageSource === 'file'}
                          onChange={() => setImageSource('file')}
                          className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Dosya Yükle</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={imageSource === 'url'}
                          onChange={() => setImageSource('url')}
                          className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">URL ile Ekle</span>
                      </label>
                    </div>
                    
                    {/* File Upload */}
                    {imageSource === 'file' && (
                      <div 
                        className={`w-64 h-64 border-2 border-dashed ${dragActive ? 'border-green-500 dark:border-green-600' : 'border-gray-300 dark:border-gray-600'} rounded-lg flex flex-col items-center justify-center p-4 hover:border-green-500 dark:hover:border-green-600 transition-colors cursor-pointer`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={handleDropzoneClick}
                      >
                        {productData.image ? (
                          <img 
                            src={URL.createObjectURL(productData.image)} 
                            alt="Ürün önizleme" 
                            className="max-h-full max-w-full object-contain" 
                          />
                        ) : (
                          <>
                            <Upload className="w-12 h-12 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                              Görsel yüklemek için tıklayın veya sürükleyin
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                              PNG, JPG, GIF (Max. 2MB)
                            </p>
                          </>
                        )}
                        <input 
                          ref={fileInputRef}
                          type="file" 
                          onChange={handleImageChange} 
                          className="hidden" 
                          id="product-image" 
                          accept="image/*" 
                        />
                      </div>
                    )}

                    {/* URL Input */}
                    {imageSource === 'url' && (
                      <div className="w-full max-w-md space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Görsel URL'si</label>
                          <input
                            type="url"
                            name="imageUrl"
                            value={productData.imageUrl}
                            onChange={handleImageUrlChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="https://example.com/image.jpg"
                          />
                        </div>
                        
                        {productData.imageUrl && (
                          <div className="w-64 h-64 mx-auto border border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center p-2 overflow-hidden">
                            <img 
                              src={productData.imageUrl}
                              alt="Ürün önizleme" 
                              className="max-h-full max-w-full object-contain"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/400x400?text=Görsel+Yüklenemedi";
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Submit Button - Hidden because we're using the header buttons */}
                <button type="submit" className="hidden">Kaydet</button>
              </form>
            )}
          </div>
        </motion.main>
      </div>
    </div>
  );
};

export default StokProductEdit; 